/**
 * Status eines Bereichs (F-01): Lebensrad-Wert mit Veraenderung, offene
 * Aufgaben, letzte Aktivitaet - auf einen Blick (Leitlinie 2). Seit V-16 die
 * einheitliche Kennzahlen-Zeile jedes Boards: dazu aktive Projekte,
 * Zielfortschritt und die Zeit in diesem Bereich diese Woche (F-28).
 */
import { Link } from 'react-router-dom';

import { skalaFarbe } from '@/domain/rating';
import { bereichKennzahlen } from '@/domain/stats/bereich';
import { stunden } from '@/domain/zeit/verteilung';
import { useZeitverteilung } from '@/modules/zeit/useZeitverteilung';
import { addDays, startOfWeek } from '@/lib/datetime';
import type { Goal, ID, Project } from '@/domain/types';
import { wannText, type BereichsStatus } from '@/domain/areas/status';
import { monatKey } from '@/domain/review/periods';
import { today } from '@/lib/datetime';
import { cn } from '@/lib/utils/cn';

export function LebensradWert({ status }: { status: BereichsStatus }) {
  if (status.lebensrad === undefined) return <span className="text-ink-faint">–</span>;
  const delta = status.vorher === undefined ? 0 : status.lebensrad - status.vorher;
  return (
    <span className="inline-flex items-center gap-1 tabular-nums" title={`Lebensrad ${status.lebensrad} von 10`}>
      <span className="size-2 rounded-full" style={{ background: skalaFarbe(status.lebensrad) }} aria-hidden="true" />
      <span className="sr-only">Lebensrad </span>{status.lebensrad}<span className="sr-only"> von 10</span>
      {delta !== 0 && (
        <span className={cn('text-[11px]', delta > 0 ? 'text-ok' : 'text-warn')} aria-label={`${delta > 0 ? 'plus' : 'minus'} ${Math.abs(delta)}`}>
          {delta > 0 ? '↑' : '↓'}{Math.abs(delta)}
        </span>
      )}
    </span>
  );
}

export function BereichStatus({ status, areaId, projects, goals }: { status: BereichsStatus; areaId: ID; projects: Project[]; goals: Goal[] }) {
  const heute = today();
  const k = bereichKennzahlen(projects, goals);
  const woche = Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(heute), i));
  const zeit = useZeitverteilung(woche, 'tatsaechlich').bereiche.find((b) => b.areaId === areaId)?.minuten ?? 0;
  const zahl = 'text-lg font-semibold tabular-nums';
  return (
    <section aria-label="Status" className="flex flex-col gap-2 rounded-lg border border-line bg-surface px-4 py-3.5">
      <h2 className="text-sm font-semibold">Status</h2>
      <dl className="grid grid-cols-3 gap-x-2 gap-y-3 text-center">
        <div className="flex flex-col gap-0.5">
          <dt className="text-[11px] text-ink-faint">Lebensrad</dt>
          <dd className="text-lg font-semibold"><LebensradWert status={status} /></dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-[11px] text-ink-faint">Offene Aufgaben</dt>
          <dd className={zahl}>{status.offeneAufgaben}</dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-[11px] text-ink-faint">Aktive Projekte</dt>
          <dd className={zahl}>{k.aktiveProjekte}</dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-[11px] text-ink-faint">Zielfortschritt</dt>
          <dd className={zahl} title={k.laufendeZiele ? `Durchschnitt aus ${k.laufendeZiele} laufenden Zielen` : 'keine laufenden Ziele'}>{k.zielFortschritt === null ? <span className="text-ink-faint">–</span> : `${k.zielFortschritt} %`}</dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-[11px] text-ink-faint">Zeit diese Woche</dt>
          <dd className={zahl} title="Termine und Zeitblöcke in diesem Bereich, wie in der Zeitverteilung">{zeit ? stunden(zeit) : <span className="text-ink-faint">–</span>}</dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-[11px] text-ink-faint">Letzte Aktivität</dt>
          <dd className="pt-1 text-sm">{wannText(status.letzteAktivitaet, heute)}</dd>
        </div>
      </dl>
      <Link to={`/rueckblick?monat=${monatKey(heute)}`} className="self-start text-[12px] text-ink-faint hover:text-ink hover:underline">
        Lebensrad bewerten
      </Link>
    </section>
  );
}
